/**
 * ATMs – Mobile Portal
 *
 * Purpose: View status, cash level; log service (replenishment/maintenance). Same API as Ketchup Portal.
 * Location: mobile-portal/src/pages/ATMs.tsx
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { atmsAPI, mobileUnitsAPI } from '@smartpay/api-client/ketchup';
import type { Atm, AtmStats, MobileUnit } from '@smartpay/api-client/ketchup';
import {
  Button,
  Card,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  useToast,
} from '@smartpay/ui';
import { Banknote, Wrench } from 'lucide-react';

const NAMIBIAN_REGIONS = [
  'Khomas', 'Erongo', 'Oshana', 'Omusati', 'Ohangwena', 'Oshikoto',
  'Kavango East', 'Kavango West', 'Zambezi', 'Kunene', 'Otjozondjupa', 'Omaheke', 'Hardap', 'Karas',
];
const ATM_STATUSES = ['operational', 'offline', 'maintenance', 'low_cash'] as const;

export default function ATMs() {
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [servicingAtm, setServicingAtm] = useState<Atm | null>(null);
  const [serviceForm, setServiceForm] = useState({ mobileUnitId: '', notes: '', cashLevelPercent: '', status: 'operational' });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['atms', region, status],
    queryFn: () => atmsAPI.getAll({ region: region || undefined, status: status || undefined }),
  });
  const { data: stats } = useQuery({
    queryKey: ['atms-stats'],
    queryFn: () => atmsAPI.getStats(),
  });
  const { data: unitsResponse } = useQuery({
    queryKey: ['mobile-units-for-atm'],
    queryFn: () => mobileUnitsAPI.getAll({}),
  });
  const rawUnits = unitsResponse as { data?: MobileUnit[] } | undefined;
  const unitsList = Array.isArray(rawUnits) ? rawUnits : (rawUnits?.data ?? []);

  const logServiceMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { mobileUnitId?: string; notes?: string; cashLevelPercent?: number; status?: string } }) =>
      atmsAPI.logService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atms'] });
      queryClient.invalidateQueries({ queryKey: ['atms-stats'] });
      setServicingAtm(null);
      setServiceForm({ mobileUnitId: '', notes: '', cashLevelPercent: '', status: 'operational' });
    },
    onError: (e) => toast({ title: 'Log service failed', description: e instanceof Error ? e.message : 'Please try again.', variant: 'destructive' }),
  });

  const handleLogService = () => {
    if (!servicingAtm) return;
    logServiceMutation.mutate({
      id: servicingAtm.id,
      body: {
        mobileUnitId: serviceForm.mobileUnitId.trim() || undefined,
        notes: serviceForm.notes.trim() || undefined,
        cashLevelPercent: serviceForm.cashLevelPercent ? Number(serviceForm.cashLevelPercent) : undefined,
        status: serviceForm.status,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">ATMs</h1>
        <p className="text-muted-foreground">View status, cash level; log maintenance and replenishment</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Total ATMs</p>
            <p className="text-2xl font-semibold">{(stats as AtmStats).total}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Operational</p>
            <p className="text-2xl font-semibold text-green-600">{(stats as AtmStats).operational}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Offline</p>
            <p className="text-2xl font-semibold text-red-600">{(stats as AtmStats).offline}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Maintenance</p>
            <p className="text-2xl font-semibold text-amber-600">{(stats as AtmStats).maintenance}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Low cash</p>
            <p className="text-2xl font-semibold text-orange-600">{(stats as AtmStats).lowCash}</p>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-4">
        <p className="text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{list.length}</span> ATMs
        </p>
        <div className="flex gap-2">
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All regions</option>
            {NAMIBIAN_REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All statuses</option>
            {ATM_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <Dialog open={!!servicingAtm} onOpenChange={(open) => { if (!open) setServicingAtm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Log ATM service
            </DialogTitle>
            <DialogDescription>
              Record replenishment or maintenance. Ketchup admins see this on ATM Management.
            </DialogDescription>
          </DialogHeader>
          {servicingAtm && (
            <div className="space-y-3 text-sm">
              <p className="font-medium">{servicingAtm.terminalId} – {servicingAtm.location}</p>
              <label className="block font-medium">Mobile unit (optional)</label>
              <select
                value={serviceForm.mobileUnitId}
                onChange={(e) => setServiceForm((f) => ({ ...f, mobileUnitId: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">—</option>
                {unitsList.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <label className="block font-medium">Notes</label>
              <Input
                placeholder="e.g. Replenished, parts replaced"
                value={serviceForm.notes}
                onChange={(e) => setServiceForm((f) => ({ ...f, notes: e.target.value }))}
              />
              <label className="block font-medium">Cash level % (0–100)</label>
              <Input
                type="number"
                min={0}
                max={100}
                placeholder="e.g. 100"
                value={serviceForm.cashLevelPercent}
                onChange={(e) => setServiceForm((f) => ({ ...f, cashLevelPercent: e.target.value }))}
              />
              <label className="block font-medium">Status after service</label>
              <select
                value={serviceForm.status}
                onChange={(e) => setServiceForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {ATM_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setServicingAtm(null)}>Cancel</Button>
            <Button
              disabled={logServiceMutation.isPending}
              onClick={handleLogService}
            >
              {logServiceMutation.isPending ? 'Logging…' : 'Log service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="p-0">
          {isLoading ? (
            <div className="p-6 text-center text-muted-foreground">Loading ATMs...</div>
          ) : list.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Banknote className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p>No ATMs found. Ensure the backend is running and seeded with ATM locations, or check your connection.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Terminal ID</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Cash %</TableHead>
                  <TableHead>Last serviced</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(list as Atm[]).map((atm) => (
                  <TableRow key={atm.id}>
                    <TableCell className="font-medium">{atm.terminalId}</TableCell>
                    <TableCell>{atm.location}</TableCell>
                    <TableCell>{atm.region ?? '—'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${atm.status === 'operational' ? 'bg-green-100 text-green-800' : atm.status === 'offline' ? 'bg-red-100 text-red-800' : atm.status === 'maintenance' ? 'bg-amber-100 text-amber-800' : 'bg-orange-100 text-orange-800'}`}>
                        {atm.status}
                      </span>
                    </TableCell>
                    <TableCell>{atm.cashLevelPercent != null ? `${atm.cashLevelPercent}%` : '—'}</TableCell>
                    <TableCell>{atm.lastServicedAt ? new Date(atm.lastServicedAt).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => { setServicingAtm(atm); setServiceForm({ mobileUnitId: atm.mobileUnitId ?? '', notes: '', cashLevelPercent: atm.cashLevelPercent != null ? String(atm.cashLevelPercent) : '', status: atm.status }); }}>
                        <Wrench className="h-4 w-4 mr-1" /> Log service
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
